import {
  Accordion,
  AccordionContent,
  AccordionItem as BaseAccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { ATTRIBUTE_MAPPING } from '@/lib/constants'

import { DataType, NodeType } from '../graph/types'
import { getNodeLabel } from '../graph/force-graph-data'

interface SecondaryEntity extends NodeType {
  data: DataType
  feeds: string[]
  [key: string]: any
}

interface AccordionItemInterface {
  data: DataType
  feed: string
  title?: string
}

type AttributeData = string | boolean | SecondaryEntity | SecondaryEntity[]

const formatTitle = (value: string) => {
  if (ATTRIBUTE_MAPPING[value]) {
    return ATTRIBUTE_MAPPING[value]
  }

  return value
    .replace(/_/g, ' ')
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

const renderAttribute = (attributeData: AttributeData | undefined, title: string) => {
  if (
    attributeData === null ||
    attributeData === undefined ||
    (Array.isArray(attributeData) && typeof attributeData[0] === 'string')
  ) {
    return null
  }

  if (typeof attributeData === 'string' || typeof attributeData === 'boolean') {
    return (
      <div key={title}>
        <p className="text-xs text-[#828282] first-letter:capitalize">{formatTitle(title)}</p>
        <p className="mt-1 text-sm font-normal first-letter:capitalize">{String(attributeData)}</p>
      </div>
    )
  }

  if (Array.isArray(attributeData)) {
    return (
      <div key={title}>
        <p className="text-xs text-[#828282] first-letter:capitalize mb-1">{formatTitle(title)}</p>

        {attributeData.map((item, index) => {
          const feeds = item?.feeds
          if (!feeds || !Array.isArray(feeds)) {
            return null
          }

          return (
            <Accordion type="single" collapsible className="w-full mb-2" key={index}>
              <p className="mb-1">{getNodeLabel(item)}</p>
              {feeds.map((feed, feedIndex) => (
                <NestedAccordionItem data={item.data} feed={feed as string} key={feedIndex} />
              ))}
            </Accordion>
          )
        })}
      </div>
    )
  }

  return (
    <div key={`${title}`}>
      <p className="text-xs text-[#828282] first-letter:capitalize mb-1">{formatTitle(title)}</p>
      <Accordion type="single" collapsible className="w-full" key={`${title}`}>
        <p className="mb-1">{getNodeLabel(attributeData)}</p>
        {Object.entries(attributeData.feeds).map(([feedKey, feed]) => (
          <NestedAccordionItem data={attributeData.data} feed={feed as string} key={feedKey} />
        ))}
      </Accordion>
    </div>
  )
}

export default function AccordionItem({ data, feed }: AccordionItemInterface) {
  return (
    <BaseAccordionItem value={feed}>
      <AccordionTrigger>{formatTitle(feed)}</AccordionTrigger>
      <AccordionContent>
        <div className="my-4">
          <div className="grid grid-cols-1 gap-4">
            {data &&
              Object.entries(data).map(([key, value]) => renderAttribute(value?.[feed], key))}
          </div>
        </div>
      </AccordionContent>
    </BaseAccordionItem>
  )
}

function NestedAccordionItem({ data, feed }: AccordionItemInterface) {
  return (
    <BaseAccordionItem value={feed} className="border-none">
      <AccordionTrigger className="bg-slate-100 p-0 pl-2 flex-row-reverse [&_svg]:ml-2 [&_svg]:mr-0 justify-end gap-2">
        {formatTitle(feed)}
      </AccordionTrigger>
      <AccordionContent>
        <div className="ml-6 mt-4 pl-4 border-l">
          <div className="grid grid-cols-1 gap-4">
            {data &&
              Object.entries(data).map(([key, value]) => renderAttribute(value?.[feed], key))}
          </div>
        </div>
      </AccordionContent>
    </BaseAccordionItem>
  )
}
